// frontend/src/components/features/AddGameModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { X, AlertTriangle, Plus } from "lucide-react";
import { httpRequest } from "../../utils/httpClient";
import { API_BASE } from "../../config/api";
import { authService } from "../../services/auth";
import { PREFERENCE_OPTIONS } from "../common/PreferenceIcons";
import GameSearchDropdown from "../common/GameSearchDropdown";
import logger from '../../utils/logger';
import './ModalComponents.css';

const OWNERSHIP_OPTIONS = [
  { value: "owned", label: "I have this" },
  { value: "interested", label: "Interested" },
  { value: "excluded", label: "Not for me" },
];

const ADDITIONAL_TAGS = ["Too much admin", "To Simulate", "Try First", "Buy", "Needs More Players"];

const SHELF_STATUSES = new Set(["owned", "interested", "excluded"]);

// Normalizes a raw ownership_status to a shelf value (owned/interested/excluded)
// or null when no shelf is selected (covers the 'no_shelf' sentinel as well as
// legacy 'played' data).
const normaliseOwnershipStatus = (status) => {
  const s = (status || "").toLowerCase();
  return SHELF_STATUSES.has(s) ? s : null;
};

// has_played is independent of ownership_status. Falls back to inferring from
// the shelf status when has_played isn't provided: a recognized shelf status
// (owned/interested/excluded) implies false by default, while no shelf status
// at all (brand new game, or legacy 'played'/'no_shelf' data) implies true —
// matching the legacy default of a fresh modal opening with Played active.
const deriveHasPlayed = (status, hasPlayed) => {
  if (hasPlayed !== undefined && hasPlayed !== null) return Boolean(hasPlayed);
  const s = (status || "").toLowerCase();
  return !SHELF_STATUSES.has(s);
};

/**
 * AddGameModal - Self-contained modal for adding games to collection
 *
 * Props:
 * - game: Object - Game data {id/game_id, name, thumbnail, ...}
 * - isOpen: Boolean - Controls modal visibility
 * - onClose: Function - Called when modal is closed (cancel or backdrop click)
 * - onSave: Function - Called when save is successful, receives saved game data
 * - onLaunchSession: Function - Optional callback invoked after saving a game marked as played or owned, to open a session form
 * - initialData: Object - Optional initial form values {preference, ownership_status, has_played, additional_tags}
 * - user: Object - Current user object with id
 * - collection: Array - User's current collection to check for duplicates
 * - forceHasPlayed: Boolean - When true, has_played is locked to true and the toggle is disabled
 *   (used by the session-rating flow, where logging a session always implies has_played=true)
 */
function AddGameModal({
  game,
  isOpen,
  onClose,
  onSave,
  onLaunchSession,
  initialData = {},
  user,
  collection = [],
  forceHasPlayed = false,
}) {
  const [formData, setFormData] = useState({
    preference: null,
    ownership_status: null,
    has_played: forceHasPlayed,
    additional_tags: [],
    comments: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [existingCollectionData, setExistingCollectionData] = useState(undefined);
  const [isUserModified, setIsUserModified] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [userCollectionTags, setUserCollectionTags] = useState([]);

  // Search state for when no game is pre-selected
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  // Sync selectedGame with game prop when it changes
  useEffect(() => {
    if (game && (!selectedGame || selectedGame?.game_id !== game.game_id)) {
      setSelectedGame(game);
    }
  }, [game, selectedGame]);

  // Initialize form data when modal opens (only once per open/close cycle)
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      const currentGame = selectedGame || game;
      if (currentGame) {
        const rawStatus = initialData.ownership_status || currentGame.ownership_status;
        const rawHasPlayed = initialData.has_played !== undefined ? initialData.has_played : currentGame.has_played;
        setFormData({
          preference: initialData.preference !== undefined ? initialData.preference : (currentGame.preference ?? null),
          ownership_status: normaliseOwnershipStatus(rawStatus),
          has_played: forceHasPlayed || deriveHasPlayed(rawStatus, rawHasPlayed),
          additional_tags: initialData.additional_tags || currentGame.additional_tags || [],
          comments: initialData.comments || currentGame.comments || "",
        });
      }
      setHasInitialized(true);
      setIsUserModified(false);
      setError(null);
      setIsSaving(false);
    }
    // Reset initialization flag when modal closes
    if (!isOpen && hasInitialized) {
      setHasInitialized(false);
      setExistingCollectionData(undefined);
      setIsUserModified(false);
      // Reset search state when modal closes
      setSearchQuery("");
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSelectedGame(null); // Reset to null, not game prop
    }
  }, [isOpen, game, selectedGame, initialData?.ownership_status, initialData?.has_played, initialData?.preference, initialData?.additional_tags, initialData?.comments, hasInitialized, forceHasPlayed]);

  // Load existing collection data for the selected game
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    const currentGame = selectedGame || game;
    if (!currentGame) {
      setExistingCollectionData(null);
      return;
    }
    const gameId = currentGame.game_id || currentGame.id;
    const fromCollection = Array.isArray(collection) && collection.find(
      (g) => String(g.game_id || g.id) === String(gameId)
    );
    if (fromCollection) {
      setExistingCollectionData({
        ownership_status: fromCollection.ownership_status || null,
        has_played: fromCollection.has_played,
        preference: fromCollection.preference !== undefined ? fromCollection.preference : null,
        additional_tags: fromCollection.additional_tags || [],
        comments: fromCollection.comments || "",
      });
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    let cancelled = false;

    const fetchExisting = async () => {
      try {
        const res = await httpRequest(`${API_BASE}/profile/collection/check`, {
          method: "POST",
          headers: {
            ...authService.getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: user.id, game_id: gameId }),
          signal: controller.signal,
        });
        if (cancelled) return;
        if (res.ok && typeof res.json === 'function') {
          const data = await res.json();
          if (data?.ownership_status) {
            setExistingCollectionData({
              ownership_status: data.ownership_status,
              has_played: data.has_played,
              preference: data.preference !== undefined ? data.preference : null,
              additional_tags: data.additional_tags || [],
              comments: data.comments || "",
            });
          } else {
            setExistingCollectionData(null);
          }
        } else {
          setExistingCollectionData(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.name !== 'AbortError') {
          logger.error('[AddGameModal] Failed to load existing collection data:', err);
        }
        setExistingCollectionData(null);
      } finally {
        clearTimeout(timeoutId);
      }
    };
    fetchExisting();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isOpen, game, selectedGame, user, collection]);

  // Load all tags from the user's collection when a full collection prop isn't provided,
  // so custom tags created on one game can be reused on any other game in the collection.
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    if (Array.isArray(collection) && collection.length > 0) {
      const tags = new Set();
      collection.forEach((g) => {
        (g.additional_tags || []).forEach((tag) => tags.add(tag));
      });
      setUserCollectionTags(Array.from(tags));
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    let cancelled = false;

    const fetchUserTags = async () => {
      try {
        const res = await httpRequest(`${API_BASE}/profile/collection?sort_by=added_at&order=desc`, {
          headers: authService.getAuthHeaders(),
          signal: controller.signal,
        });
        if (cancelled) return;
        if (res.ok && typeof res.json === 'function') {
          const data = await res.json();
          const tags = new Set();
          (data || []).forEach((g) => {
            (g.additional_tags || []).forEach((tag) => tags.add(tag));
          });
          setUserCollectionTags(Array.from(tags));
        }
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        logger.error('[AddGameModal] Failed to load user collection tags:', err);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    fetchUserTags();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isOpen, user, collection]);

  // Pre-populate form fields from the backend or collection prop when the parent
  // did not explicitly provide them, unless the user has already edited the form.
  useEffect(() => {
    if (!existingCollectionData || isUserModified) return;
    setFormData((prev) => {
      const next = { ...prev };
      if (initialData?.ownership_status === undefined && existingCollectionData?.ownership_status !== undefined) {
        next.ownership_status = normaliseOwnershipStatus(existingCollectionData.ownership_status);
      }
      if (!forceHasPlayed && initialData?.has_played === undefined) {
        next.has_played = deriveHasPlayed(existingCollectionData?.ownership_status, existingCollectionData?.has_played);
      }
      if (initialData?.preference === undefined && existingCollectionData?.preference !== undefined) {
        next.preference = existingCollectionData.preference;
      }
      if (initialData?.additional_tags === undefined && existingCollectionData?.additional_tags !== undefined) {
        next.additional_tags = existingCollectionData.additional_tags;
      }
      if (initialData?.comments === undefined && existingCollectionData?.comments !== undefined) {
        next.comments = existingCollectionData.comments;
      }
      return next;
    });
  }, [existingCollectionData, isUserModified, initialData?.ownership_status, initialData?.has_played, initialData?.preference, initialData?.additional_tags, initialData?.comments, forceHasPlayed]);

  // Check if game already exists in collection
  const isGameInCollection = useMemo(() => {
    const currentGame = selectedGame || game;
    if (!currentGame || !Array.isArray(collection) || collection.length === 0) return false;
    const gameId = currentGame.game_id || currentGame.id;
    return collection.some(game => (game.game_id || game.id) === gameId);
  }, [selectedGame, game, collection]);

  const availableTags = useMemo(() => {
    const tags = new Set([...ADDITIONAL_TAGS, ...userCollectionTags]);
    formData.additional_tags.forEach((tag) => tags.add(tag));
    return Array.from(tags).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [userCollectionTags, formData.additional_tags]);

  const handlePreferenceChange = useCallback((preference) => {
    setIsUserModified(true);
    setFormData((prev) => ({ ...prev, preference: prev.preference === preference ? null : preference }));
  }, []);

  const handleOwnershipChange = useCallback((ownership_status) => {
    setIsUserModified(true);
    setFormData((prev) => {
      const isUnselecting = prev.ownership_status === ownership_status;
      if (isUnselecting) {
        // Deselecting a shelf button clears the shelf selection; has_played is
        // independent and is left untouched.
        return {
          ...prev,
          ownership_status: null,
        };
      }
      return {
        ...prev,
        ownership_status,
        // For 'interested' or 'excluded', preference stays null
        preference:
          ownership_status === "interested" || ownership_status === "excluded"
            ? null
            : prev.preference,
      };
    });
  }, []);

  const handlePlayedToggle = useCallback(() => {
    if (forceHasPlayed) return;
    setIsUserModified(true);
    setFormData((prev) => ({ ...prev, has_played: !prev.has_played }));
  }, [forceHasPlayed]);

  const handleTagToggle = useCallback((tag) => {
    setIsUserModified(true);
    setFormData((prev) => ({
      ...prev,
      additional_tags: prev.additional_tags.includes(tag)
        ? prev.additional_tags.filter((t) => t !== tag)
        : [...prev.additional_tags, tag],
    }));
  }, []);

  const handleAddCustomTag = useCallback(() => {
    const rawTag = newTagInput.trim();
    if (!rawTag) return;
    setIsUserModified(true);
    setFormData((prev) => ({
      ...prev,
      additional_tags: prev.additional_tags.includes(rawTag)
        ? prev.additional_tags
        : [...prev.additional_tags, rawTag],
    }));
    setNewTagInput("");
  }, [newTagInput]);

  const handleCommentsChange = useCallback((value) => {
    setIsUserModified(true);
    setFormData((prev) => ({ ...prev, comments: value }));
  }, []);

  // Game search functionality
  const handleGameSearch = useCallback(async (query) => {
    setSearchQuery(query);

    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    // Show dropdown immediately when user types 2+ characters
    setShowSearchDropdown(true);
    setIsSearching(true);

    try {
      const res = await httpRequest(`${API_BASE}/games/search?q=${encodeURIComponent(query)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.games || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      logger.error("Game search failed:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleGameSelect = useCallback((selectedGameFromSearch) => {
    setSelectedGame(selectedGameFromSearch);
    setShowSearchDropdown(false);
    setSearchQuery(selectedGameFromSearch.name);
    setSearchResults([]);
    setExistingCollectionData(undefined);
    setIsUserModified(false);
    // Update form data with selected game (no need to reset initialization)
    const searchOwnership = normaliseOwnershipStatus(selectedGameFromSearch.ownership_status);
    setFormData({
      preference: selectedGameFromSearch.preference ?? null,
      ownership_status: searchOwnership,
      has_played: forceHasPlayed || deriveHasPlayed(selectedGameFromSearch.ownership_status, selectedGameFromSearch.has_played),
      additional_tags: selectedGameFromSearch.additional_tags || [],
      comments: selectedGameFromSearch.comments || "",
    });
  }, [forceHasPlayed]);

  const effectiveHasPlayed = forceHasPlayed || formData.has_played;
  const effectiveOwnershipStatus = formData.ownership_status || "no_shelf";
  const isNoShelfWithoutPlayed = effectiveOwnershipStatus === "no_shelf" && !effectiveHasPlayed;

  const handleSave = useCallback(async () => {
    if (isRemoving) return;
    if (!user?.id || !(selectedGame || game)) return;
    if (isNoShelfWithoutPlayed) return;

    setIsSaving(true);
    setError(null);

    try {
      const currentGame = selectedGame || game;
      const gameId = currentGame.game_id || currentGame.id;

      const payload = {
        game_id: gameId,
        preference: formData.preference,
        ownership_status: effectiveOwnershipStatus,
        has_played: effectiveHasPlayed,
        user_id: user.id,
        comments: formData.comments || "",
      };

      if (formData.additional_tags?.length > 0) {
        payload.additional_tags = formData.additional_tags;
      }


      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const savedGame = {
          id: gameId,
          game_id: gameId,
          name: currentGame.name,
          thumbnail: currentGame.thumbnail,
          preference: formData.preference,
          ownership_status: effectiveOwnershipStatus,
          has_played: effectiveHasPlayed,
          additional_tags: formData.additional_tags,
          comments: formData.comments || "",
          isShared: currentGame.isShared || false,
        };

        onSave?.(savedGame);

        if (onLaunchSession && (effectiveHasPlayed || effectiveOwnershipStatus === 'owned')) {
          onLaunchSession(savedGame);
        }

        onClose?.();
      } else {
        let errorMessage = "Failed to update library";
        try {
          const errorData = await res.json();
          if (typeof errorData === "string") {
            errorMessage = errorData;
          } else if (errorData?.detail) {
            errorMessage = Array.isArray(errorData.detail)
              ? errorData.detail.map((err) =>
                  typeof err === "string" ? err : err.msg || err.message || JSON.stringify(err)
                ).join(", ")
              : errorData.detail;
          } else {
            errorMessage = errorData.message || errorData.error || JSON.stringify(errorData) || "Failed to add game";
          }
        } catch {
          // Use default error message
        }
        setError(errorMessage);
      }
    } catch (err) {
      logger.error("Failed to save game:", err);
      setError("Failed to save game. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [user, selectedGame, game, formData, onSave, onLaunchSession, onClose, isRemoving, isNoShelfWithoutPlayed, effectiveOwnershipStatus, effectiveHasPlayed]);

  const handleRemoveFromCollection = useCallback(async () => {
    if (isSaving) return;
    if (!user?.id || !(selectedGame || game)) return;

    setIsRemoving(true);
    setError(null);

    try {
      const currentGame = selectedGame || game;
      const gameId = currentGame.game_id || currentGame.id;

      const res = await httpRequest(`${API_BASE}/profile/collection/${gameId}`, {
        method: "DELETE",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        onClose?.();
      } else {
        let errorMessage = "Failed to remove game";
        try {
          if (typeof res.json === "function") {
            const errorData = await res.json();
            if (typeof errorData === "string") {
              errorMessage = errorData;
            } else if (errorData?.detail) {
              errorMessage = Array.isArray(errorData.detail)
                ? errorData.detail.map((err) =>
                    typeof err === "string" ? err : err.msg || err.message || JSON.stringify(err)
                  ).join(", ")
                : errorData.detail;
            } else {
              errorMessage = errorData?.message || errorData?.error || JSON.stringify(errorData) || "Failed to remove game";
            }
          } else {
            errorMessage = await res.text?.() || errorMessage;
          }
        } catch {
          // Use default error message
        }
        setError(errorMessage);
      }
    } catch (err) {
      logger.error("Failed to remove game:", err);
      setError("Failed to remove game. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  }, [user, selectedGame, game, isSaving, onClose]);

  const handleBackdropClick = useCallback(() => {
    if (!isSaving && !isRemoving) {
      onClose?.();
    }
  }, [isSaving, isRemoving, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && !isSaving && !isRemoving) {
        onClose?.();
      }
    },
    [isSaving, isRemoving, onClose]
  );

  if (!isOpen) {
    return null;
  }

  const currentGame = selectedGame || game;

  const gameId = currentGame?.game_id || currentGame?.id;
  const gameName = currentGame?.name || `Game ${gameId || ""}`.trim();

  const isSaveDisabled = isSaving || isRemoving || !currentGame || isNoShelfWithoutPlayed;

  const isGameAlreadyInCollection = isGameInCollection || (existingCollectionData !== undefined && existingCollectionData !== null);

  const removeLinkText = (() => {
    const status = normaliseOwnershipStatus(existingCollectionData?.ownership_status ?? formData.ownership_status);
    if (status === "interested") return "Not interested in this game anymore";
    if (status === "excluded") return "Remove from list: Not for me";
    return "Remove from library";
  })();

  return (
    <div
      className="confirm-dialog-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-labelledby="add-game-modal-title"
      aria-describedby="add-game-modal-content"
    >
      <div
        className="confirm-dialog add-game-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-game-modal-header">
          <h3 id="add-game-modal-title" className="add-game-modal-title">
            Add to Library
          </h3>
          <button
            onClick={onClose}
            disabled={isSaving || isRemoving}
            className="modal-close"
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div
          id="add-game-modal-content"
          className="confirm-dialog-content add-game-modal-content"
        >
          {/* Game Search Section - Only show when no game is pre-selected */}
          {!game && (
            <div className="form-group">
              <label className="add-game-modal-label">
                Search for a game:
              </label>
              <div className="game-search-container">
                <GameSearchDropdown
                  searchQuery={searchQuery}
                  onSearchChange={handleGameSearch}
                  isVisible={showSearchDropdown}
                  onVisibilityChange={setShowSearchDropdown}
                  searchResults={searchResults}
                  isLoading={isSearching}
                  onGameSelect={handleGameSelect}
                  showImages={true}
                  placeholder="Search for games..."
                  autoFocus={typeof window !== 'undefined' && window.innerWidth > 768}
                    inputStyle={{
                    backgroundColor: (selectedGame || game) ? "var(--bg-secondary, #f5f5f5)" : "var(--bg-primary, white)",
                    cursor: (selectedGame || game) ? "default" : "text",
                    color: "var(--text-primary, #333)"
                  }}
                  disabled={!!(selectedGame || game)}
                />
              </div>
            </div>
          )}

          {/* Game Details Section - Show when game is selected */}
          {currentGame && (
            <>
              <div className="add-game-modal-game-details">
                <img
                  src={currentGame.thumbnail || "/placeholder-game.png"}
                  alt={gameName}
                  className="add-game-modal-game-thumbnail"
                />
                <h4 className="add-game-modal-game-name">
                  {gameName}
                </h4>
              </div>

              {/* Overwrite Message - Show when game is already in collection */}
              {isGameInCollection && (
                <div className="add-game-modal-warning">
                  <AlertTriangle size={16} /> This game is already in your collection. The current entry will be overwritten with your new settings.
                </div>
              )}
            </>
          )}

          {/* Form Section - Only show when game is selected */}
          {currentGame && (
            <>
              <div
                style={{
                  marginBottom: "1rem",
                  backgroundColor: "var(--bg-primary, white)",
                  padding: "0.75rem",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "bold",
                      color: "var(--text-primary, #333)",
                    }}
                  >
                    Shelf category
                  </label>
                  <button
                    type="button"
                    onClick={handlePlayedToggle}
                    disabled={isSaving || forceHasPlayed}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      cursor: (isSaving || forceHasPlayed) ? "not-allowed" : "pointer",
                      opacity: isSaving ? 0.7 : 1,
                      border: effectiveHasPlayed
                        ? "1px solid var(--pista-green-primary, #2E7D32)"
                        : "1px dashed var(--border-color, #aaa)",
                      backgroundColor: effectiveHasPlayed
                        ? "var(--pista-green-primary, #2E7D32)"
                        : "var(--bg-secondary, #e0e0e0)",
                      color: effectiveHasPlayed ? "white" : "var(--text-primary, #333)",
                    }}
                  >
                    {effectiveHasPlayed ? (
                      forceHasPlayed ? "Played" : (
                        <>
                          Played <X size={12} />
                        </>
                      )
                    ) : (
                      "+ Played"
                    )}
                  </button>
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-secondary, #666)",
                    marginBottom: "0.5rem",
                  }}
                >
                  {effectiveHasPlayed
                    ? "Select a shelf category if it applies (optional)"
                    : "Select one of the following:"}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    backgroundColor: "transparent",
                    padding: "0.5rem 0.5rem 0",
                    borderRadius: "8px",
                  }}
                >
                  {OWNERSHIP_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOwnershipChange(option.value)}
                      disabled={isSaving}
                      style={{
                        flex: 1,
                        padding: "0.5rem",
                        border: `1px solid ${
                          formData.ownership_status === option.value
                            ? "var(--pista-green-primary, #2E7D32)"
                            : "var(--border-color, #ddd)"
                        }`,
                        borderRadius: "8px",
                        fontSize: "0.85rem",
                        backgroundColor:
                          formData.ownership_status === option.value
                            ? "var(--pista-green-primary, #2E7D32)"
                            : "var(--bg-secondary, white)",
                        color:
                          formData.ownership_status === option.value
                            ? "white"
                            : "var(--text-primary, #333)",
                        cursor: isSaving ? "not-allowed" : "pointer",
                        fontWeight: formData.ownership_status === option.value ? "bold" : "normal",
                        opacity: isSaving ? 0.7 : 1,
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {isGameAlreadyInCollection && (
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: 0,
                      marginBottom: 0,
                      paddingBottom: 0,
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleRemoveFromCollection}
                      disabled={isSaving || isRemoving}
                      style={{
                        display: "inline-block",
                        background: "none",
                        border: "none",
                        color: "var(--error-color, #f44336)",
                        cursor: isSaving || isRemoving ? "not-allowed" : "pointer",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        padding: 0,
                        margin: 0,
                        whiteSpace: "nowrap",
                        opacity: isSaving || isRemoving ? 0.7 : 1,
                      }}
                    >
                      {isRemoving ? "Removing..." : removeLinkText}
                    </button>
                  </div>
                )}
              </div>

              {formData.ownership_status === "excluded" && (
                <div
                  style={{
                    marginBottom: "1rem",
                    backgroundColor: "var(--bg-secondary, white)",
                    padding: "0.75rem",
                    borderRadius: "8px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "bold",
                      color: "var(--text-primary, #333)",
                    }}
                    htmlFor="add-game-modal-comments"
                  >
                    Comments (optional):
                  </label>
                  <textarea
                    id="add-game-modal-comments"
                    value={formData.comments}
                    onChange={(e) => handleCommentsChange(e.target.value)}
                    disabled={isSaving}
                    placeholder="Why is this game not for you?"
                    style={{
                      width: "100%",
                      minHeight: "80px",
                      padding: "0.5rem",
                      borderRadius: "6px",
                      border: "1px solid var(--border-color, #ddd)",
                      backgroundColor: "var(--bg-primary, white)",
                      color: "var(--text-primary, #333)",
                      fontSize: "0.875rem",
                      resize: "vertical",
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  />
                </div>
              )}

              {formData.ownership_status !== "interested" &&
                formData.ownership_status !== "excluded" && (
                  <div
                    className="form-group"
                    style={{
                      backgroundColor: "var(--bg-secondary, white)",
                      padding: "0.75rem",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                    }}
                  >
                    <label className="add-game-modal-label">
                      Your Preference:
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        backgroundColor: "var(--bg-secondary, white)",
                        padding: "0.5rem",
                        borderRadius: "8px",
                      }}
                    >
                      {PREFERENCE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handlePreferenceChange(option.value)}
                          disabled={isSaving}
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.25rem",
                            padding: "0.5rem",
                            border: `2px solid ${
                              formData.preference === option.value
                                ? option.color
                                : "var(--border-color, #ddd)"
                            }`,
                            borderRadius: "8px",
                            backgroundColor:
                              formData.preference === option.value
                                ? option.color
                                : "var(--bg-secondary, white)",
                            color: formData.preference === option.value ? "white" : "var(--text-primary, #333)",
                            cursor: isSaving ? "not-allowed" : "pointer",
                            fontWeight: formData.preference === option.value ? "bold" : "normal",
                            fontSize: "0.65rem",
                            opacity: isSaving ? 0.7 : 1,
                          }}
                        >
                          {React.cloneElement(option.icon, { size: 24 })}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {formData.ownership_status === "interested" && (
                <div
                  style={{
                    marginBottom: "1rem",
                    backgroundColor: "var(--bg-secondary, white)",
                    padding: "0.75rem",
                    borderRadius: "8px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "bold",
                      color: "var(--text-primary, #333)",
                    }}
                  >
                    Additional Tags:
                  </label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      backgroundColor: "var(--bg-secondary, white)",
                      padding: "0.5rem",
                      borderRadius: "8px",
                    }}
                  >
                    {availableTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        disabled={isSaving}
                        style={{
                          padding: "0.25rem 0.5rem",
                          border: `1px solid ${
                            formData.additional_tags.includes(tag)
                              ? "var(--pista-green-primary, #2E7D32)"
                              : "var(--border-color, #ddd)"
                          }`,
                          borderRadius: "12px",
                          fontSize: "0.8rem",
                          backgroundColor: formData.additional_tags.includes(tag)
                            ? "var(--pista-green-primary, #2E7D32)"
                            : "transparent",
                          color: formData.additional_tags.includes(tag)
                            ? "white"
                            : "var(--text-primary, #333)",
                          cursor: isSaving ? "not-allowed" : "pointer",
                          fontWeight: formData.additional_tags.includes(tag) ? "bold" : "normal",
                          opacity: isSaving ? 0.7 : 1,
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowNewTagInput(true)}
                      disabled={isSaving}
                      title="Add a new tag"
                      aria-label="Add a new tag"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0.25rem 0.5rem",
                        border: "1px solid var(--border-color, #ddd)",
                        borderRadius: "12px",
                        backgroundColor: "var(--bg-secondary, white)",
                        color: "var(--text-primary, #333)",
                        fontSize: "0.8rem",
                        cursor: isSaving ? "not-allowed" : "pointer",
                        opacity: isSaving ? 0.7 : 1,
                        minHeight: "26px",
                      }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {showNewTagInput && (
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        marginTop: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={newTagInput}
                        onChange={(e) => setNewTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCustomTag();
                          }
                        }}
                        autoFocus
                        disabled={isSaving}
                        placeholder="New tag..."
                        style={{
                          flex: 1,
                          padding: "0.5rem",
                          border: "1px solid var(--border-color, #ddd)",
                          borderRadius: "6px",
                          backgroundColor: "var(--bg-primary, white)",
                          color: "var(--text-primary, #333)",
                          fontSize: "0.875rem",
                          minHeight: "36px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomTag}
                        disabled={!newTagInput.trim() || isSaving}
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "6px",
                          border: "none",
                          backgroundColor: "var(--pista-green-primary, #2E7D32)",
                          color: "white",
                          fontSize: "0.875rem",
                          cursor: !newTagInput.trim() || isSaving ? "not-allowed" : "pointer",
                          opacity: !newTagInput.trim() || isSaving ? 0.7 : 1,
                          minHeight: "36px",
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
              )}

              {error && (
            <div
              style={{
                padding: "0.75rem",
                backgroundColor: "var(--error-bg, #ffebee)",
                border: "1px solid var(--error-color, #ef5350)",
                borderRadius: "4px",
                color: "var(--error-text, #c62828)",
                fontSize: "0.875rem",
              }}
            >
              {error}
            </div>
          )}
            </>
          )}
        </div>

        <div
          className="confirm-dialog-actions"
          style={{ borderTop: "1px solid var(--border-color, #ddd)" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="btn-cancel"
            style={{
              background: "var(--button-secondary-bg, #6c757d)",
              color: "var(--button-secondary-text, white)",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              cursor: isSaving ? "not-allowed" : "pointer",
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="btn-confirm"
            style={{
              background: "var(--button-primary-bg, #28a745)",
              color: "var(--button-primary-text, white)",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              cursor: isSaveDisabled ? "not-allowed" : "pointer",
              opacity: isSaveDisabled ? 0.5 : 1,
              minWidth: "80px",
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddGameModal;
